import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  buildTodoUpdate,
  todoToDraft,
  useAllTodos,
  useCreateTodo,
  useDeleteTodo,
  useToggleTodoComplete,
  useUpdateTodo,
  type TodoDraft,
  type TodoOverviewFilters,
  type TodoStatusFilter,
  type UpdateTodoVars,
} from '../lib/todos';
import { useProjects } from '../lib/projects';
import { useSections } from '../lib/sections';
import { useUsersLookup } from '../lib/lookups';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { TodoMoveFields } from '../components/TodoMoveFields';
import { TodoSectionTag } from '../components/TodoSectionTag';
import type { TodoWithUsers } from '@stromo/shared';

/** URL-Parameter ↔ Filter. Die Filter stehen in der URL, damit Zurück-
 *  Button, Reload und Link-Teilen den Stand behalten. */
const PARAM = {
  q: 'q',
  status: 'status',
  project: 'projekt',
  section: 'sektion',
  createdBy: 'von',
  pm: 'pl',
  sm: 'bl',
} as const;

const parseId = (v: string | null): number | undefined =>
  v && /^\d+$/.test(v) ? Number(v) : undefined;

function filtersFromParams(params: URLSearchParams): TodoOverviewFilters {
  const status = params.get(PARAM.status);
  const section = params.get(PARAM.section);
  return {
    status: status === 'completed' || status === 'all' ? status : 'open',
    q: params.get(PARAM.q) ?? undefined,
    project_id: parseId(params.get(PARAM.project)),
    section_id: section === 'none' ? 'none' : parseId(section),
    created_by: parseId(params.get(PARAM.createdBy)),
    project_manager_id: parseId(params.get(PARAM.pm)),
    site_manager_id: parseId(params.get(PARAM.sm)),
  };
}

export function TodosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const setParam = useCallback(
    (key: string, value: string | number | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, String(value));
          // Sektion gehört zum Projekt — beim Projektwechsel zurücksetzen.
          if (key === PARAM.project) next.delete(PARAM.section);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Freitext lokal halten und erst nach kurzer Tipp-Pause in die URL
  // schreiben — sonst geht pro Tastendruck ein Request raus.
  const [qInput, setQInput] = useState(filters.q ?? '');
  useEffect(() => {
    const trimmed = qInput.trim();
    if (trimmed === (filters.q ?? '')) return;
    const timer = window.setTimeout(() => setParam(PARAM.q, trimmed || undefined), 300);
    return () => window.clearTimeout(timer);
  }, [qInput, filters.q, setParam]);

  const groupsQ = useAllTodos(filters);
  const hasFilter =
    !!filters.q ||
    filters.status !== 'open' ||
    filters.project_id !== undefined ||
    filters.section_id !== undefined ||
    filters.created_by !== undefined ||
    filters.project_manager_id !== undefined ||
    filters.site_manager_id !== undefined;
  // Todo-Filter blenden Projekte ohne Treffer aus (siehe Server) — dann
  // pro Projekt die Trefferzahl statt nur der Gesamtzahlen zeigen.
  const hasTodoFilter =
    !!filters.q || filters.section_id !== undefined || filters.created_by !== undefined;

  const resetFilters = () => {
    setQInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Todos</div>
      </header>

      <div className="content">
        <TodoFilterBar
          filters={filters}
          qInput={qInput}
          onQInput={setQInput}
          onParam={setParam}
          onReset={hasFilter ? resetFilters : undefined}
        />

        {groupsQ.isLoading ? (
          <div className="loading">Lade …</div>
        ) : groupsQ.error ? (
          <div className="empty-state"><p>Fehler: {apiErrorMessage(groupsQ.error)}</p></div>
        ) : (groupsQ.data ?? []).length === 0 ? (
          <div className="empty-state">
            <p>{hasFilter ? 'Keine Todos für diese Filter.' : 'Keine aktiven Projekte vorhanden.'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(groupsQ.data ?? []).map((group) => (
              <ProjectGroup
                key={group.project_id}
                projectId={group.project_id}
                orderNumber={group.order_number}
                title={group.title}
                openCount={group.open_count}
                totalCount={group.total_count}
                todos={group.todos}
                status={filters.status}
                hasTodoFilter={hasTodoFilter}
                defaultSectionId={
                  typeof filters.section_id === 'number' && filters.project_id === group.project_id
                    ? filters.section_id
                    : null
                }
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

interface TodoFilterBarProps {
  filters: TodoOverviewFilters;
  qInput: string;
  onQInput: (v: string) => void;
  onParam: (key: string, value: string | number | undefined) => void;
  onReset?: () => void;
}

function TodoFilterBar({ filters, qInput, onQInput, onParam, onReset }: TodoFilterBarProps) {
  const { user } = useAuthStore();
  const users = useUsersLookup();
  const projects = useProjects({ is_archived: false });
  const sections = useSections(filters.project_id);
  const idValue = (v: number | undefined) => (v === undefined ? '' : String(v));
  const toId = (v: string) => (v ? Number(v) : undefined);
  // Mobile: Zusatzfilter einklappbar, sonst schieben 7 Felder die Liste
  // aus dem Bild. Am Desktop immer sichtbar (CSS).
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = [
    filters.project_id,
    filters.section_id,
    filters.created_by,
    filters.project_manager_id,
    filters.site_manager_id,
  ].filter((v) => v !== undefined).length;

  return (
    <div className="filter-bar todo-filter-bar">
      <input
        type="search"
        value={qInput}
        onChange={(e) => onQInput(e.target.value)}
        placeholder="Titel oder Beschreibung …"
        aria-label="Todos durchsuchen"
        className="todo-filter-bar__search"
      />

      <select
        value={filters.status}
        onChange={(e) => onParam(PARAM.status, e.target.value === 'open' ? undefined : e.target.value)}
        aria-label="Status"
      >
        <option value="open">Offen</option>
        <option value="completed">Erledigt</option>
        <option value="all">Offen + erledigt</option>
      </select>

      <button
        type="button"
        className="btn btn-secondary btn-sm todo-filter-bar__toggle"
        onClick={() => setMoreOpen((o) => !o)}
        aria-expanded={moreOpen}
      >
        {moreOpen ? '▾' : '▸'} Weitere Filter{moreActive > 0 ? ` (${moreActive})` : ''}
      </button>

      <div className={`todo-filter-bar__more${moreOpen ? ' is-open' : ''}`}>
        <select
          value={idValue(filters.project_id)}
          onChange={(e) => onParam(PARAM.project, toId(e.target.value))}
          aria-label="Projekt"
        >
          <option value="">Alle Projekte</option>
          {(projects.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.order_number} {p.title}
            </option>
          ))}
        </select>

        <select
          value={filters.section_id === undefined ? '' : String(filters.section_id)}
          onChange={(e) => onParam(PARAM.section, e.target.value || undefined)}
          disabled={filters.project_id === undefined}
          aria-label="Sektion"
          title={filters.project_id === undefined ? 'Zuerst ein Projekt wählen' : undefined}
        >
          <option value="">Alle Sektionen</option>
          <option value="none">Allgemein</option>
          {(sections.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          value={idValue(filters.created_by)}
          onChange={(e) => onParam(PARAM.createdBy, toId(e.target.value))}
          aria-label="Erstellt von"
        >
          <option value="">Alle Ersteller</option>
          {user && <option value={user.id}>Von mir erstellt</option>}
          {(users.data ?? [])
            .filter((u) => u.id !== user?.id)
            .map((u) => (
              <option key={u.id} value={u.id}>
                Von {u.first_name} {u.last_name}
              </option>
            ))}
        </select>

        <select
          value={idValue(filters.project_manager_id)}
          onChange={(e) => onParam(PARAM.pm, toId(e.target.value))}
          aria-label="Projektleiter"
        >
          <option value="">Alle Projektleiter</option>
          {(users.data ?? [])
            .filter((u) => u.role === 'project_manager' || u.role === 'admin')
            .map((u) => (
              <option key={u.id} value={u.id}>
                PL {u.first_name} {u.last_name}
              </option>
            ))}
        </select>

        <select
          value={idValue(filters.site_manager_id)}
          onChange={(e) => onParam(PARAM.sm, toId(e.target.value))}
          aria-label="Bauleiter"
        >
          <option value="">Alle Bauleiter</option>
          {(users.data ?? [])
            .filter((u) => u.role === 'site_manager' || u.role === 'admin')
            .map((u) => (
              <option key={u.id} value={u.id}>
                BL {u.first_name} {u.last_name}
              </option>
            ))}
        </select>
      </div>

      {onReset && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>
          Filter zurücksetzen
        </button>
      )}
    </div>
  );
}

interface ProjectGroupProps {
  projectId: number;
  orderNumber: string;
  title: string;
  openCount: number;
  totalCount: number;
  todos: TodoWithUsers[];
  status: TodoStatusFilter;
  hasTodoFilter: boolean;
  /** Vorbelegung fürs Sektions-Dropdown beim schnellen Anlegen. */
  defaultSectionId: number | null;
}

function ProjectGroup({
  projectId,
  orderNumber,
  title,
  openCount,
  totalCount,
  todos,
  status,
  hasTodoFilter,
  defaultSectionId,
}: ProjectGroupProps) {
  const { user } = useAuthStore();
  const isPrivileged = user?.role === 'admin' || user?.role === 'project_manager';
  const createMut = useCreateTodo(projectId);
  const updateMut = useUpdateTodo();
  const toggleMut = useToggleTodoComplete(projectId);
  const deleteMut = useDeleteTodo();
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSectionId, setNewSectionId] = useState<number | null>(defaultSectionId);
  const [adding, setAdding] = useState(false);

  const startAdding = () => {
    setNewSectionId(defaultSectionId);
    setAdding(true);
  };

  const handleQuickAdd = () => {
    const t = newTitle.trim();
    if (!t) return;
    createMut.mutate(
      { title: t, description: newDescription.trim() || null, section_id: newSectionId },
      {
        onSuccess: () => {
          setNewTitle('');
          setNewDescription('');
          setAdding(false);
        },
      },
    );
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewTitle('');
    setNewDescription('');
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '14px 20px',
          borderBottom: '1px solid var(--color-border-soft)',
          background: 'var(--color-surface-alt)',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Link
            to={`/projects/${projectId}`}
            style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-navy)' }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--color-text-soft)' }}>
              {orderNumber}
            </span>
            {' '}
            {title}
          </Link>
          <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>
            {hasTodoFilter && `${todos.length} Treffer · `}
            {openCount} offen
            {totalCount - openCount > 0 && `, ${totalCount - openCount} erledigt`}
          </div>
        </div>
        {!adding ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={startAdding}
          >
            + Todo
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={cancelAdd}
          >
            Abbrechen
          </button>
        )}
      </div>

      {adding && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border-soft)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Titel des neuen Todos …"
            autoFocus
            onKeyDown={(e) => {
              // Enter im Titel-Input: in die Beschreibung wechseln statt
              // direkt absenden — sonst wird die optionale Beschreibung
              // ständig "weggeklickt". Cmd/Ctrl+Enter sendet ab.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleQuickAdd();
              }
              if (e.key === 'Escape') cancelAdd();
            }}
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Beschreibung (optional)"
            rows={2}
            style={{ resize: 'vertical', minHeight: 36 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleQuickAdd();
              }
              if (e.key === 'Escape') cancelAdd();
            }}
          />
          <div className="todo-add-row">
            <QuickAddSectionSelect
              projectId={projectId}
              value={newSectionId}
              onChange={setNewSectionId}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleQuickAdd}
              disabled={!newTitle.trim() || createMut.isPending}
              style={{ marginLeft: 'auto' }}
            >
              {createMut.isPending ? 'Speichere …' : 'Hinzufügen'}
            </button>
          </div>
          {createMut.isError && (
            <div style={{ color: 'var(--color-danger-text)', fontSize: 12 }}>
              {apiErrorMessage(createMut.error)}
            </div>
          )}
        </div>
      )}

      {updateMut.isError && (
        <div style={{ padding: '8px 20px 0', color: 'var(--color-danger-text)', fontSize: 12 }}>
          {apiErrorMessage(updateMut.error)}
        </div>
      )}

      <div style={{ padding: '8px 14px' }}>
        {todos.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--color-text-soft)' }}>
            {status === 'open' ? 'Keine offenen Todos.' : status === 'completed' ? 'Keine erledigten Todos.' : 'Keine Todos.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
            {todos.map((t) => (
              <TodoCompactRow
                key={t.id}
                todo={t}
                currentUserId={user?.id ?? null}
                isPrivileged={isPrivileged}
                onToggle={(checked) => toggleMut.mutate({ id: t.id, completed: checked })}
                onSave={(vars) => updateMut.mutate(vars)}
                onDelete={() => deleteMut.mutate(t.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Eigene Komponente, damit die Sektionen eines Projekts erst geladen
 *  werden, wenn der Anwender dort wirklich ein Todo anlegt. */
function QuickAddSectionSelect(props: {
  projectId: number;
  value: number | null;
  onChange: (sectionId: number | null) => void;
}) {
  const sections = useSections(props.projectId);
  if ((sections.data ?? []).length === 0) return null;
  return (
    <select
      value={props.value ?? ''}
      onChange={(e) => props.onChange(e.target.value ? Number(e.target.value) : null)}
      aria-label="Sektion des neuen Todos"
    >
      <option value="">Sektion: Allgemein</option>
      {(sections.data ?? []).map((s) => (
        <option key={s.id} value={s.id}>
          Sektion: {s.name}
        </option>
      ))}
    </select>
  );
}

function TodoCompactRow(props: {
  todo: TodoWithUsers;
  currentUserId: number | null;
  isPrivileged: boolean;
  onToggle: (completed: boolean) => void;
  onSave: (vars: UpdateTodoVars) => void;
  onDelete: () => void;
}) {
  const { todo, currentUserId, isPrivileged, onToggle, onSave, onDelete } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TodoDraft>(() => todoToDraft(todo));
  const isOwner = todo.created_by === currentUserId;
  const isDone = todo.completed_at !== null;
  const canEdit = (isOwner || isPrivileged) && !isDone;
  const canDelete = isPrivileged || (isOwner && !isDone);

  const startEdit = () => {
    setDraft(todoToDraft(todo));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(todoToDraft(todo));
  };

  const commitEdit = () => {
    if (!draft.title.trim()) return;
    const vars = buildTodoUpdate(todo, draft);
    if (vars) onSave(vars);
    setEditing(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '8px 10px',
        borderRadius: 6,
        background: isDone ? 'var(--color-surface-alt)' : 'transparent',
      }}
    >
      <input
        type="checkbox"
        checked={isDone}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', marginTop: 2 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitEdit(); }
                if (e.key === 'Escape') cancelEdit();
              }}
              style={{ width: '100%', fontSize: 14, fontWeight: 600, marginBottom: 6 }}
            />
            <textarea
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="Beschreibung (optional)"
              rows={2}
              style={{ width: '100%', resize: 'vertical', minHeight: 32 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitEdit(); }
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <TodoMoveFields
              projectId={draft.projectId}
              sectionId={draft.sectionId}
              onChange={({ projectId, sectionId }) => setDraft((d) => ({ ...d, projectId, sectionId }))}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn-primary btn-sm" onClick={commitEdit}>
                Speichern
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEdit}>
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 14,
                color: 'var(--color-navy)',
                textDecoration: isDone ? 'line-through' : 'none',
                cursor: canEdit ? 'text' : 'default',
                fontWeight: 600,
              }}
              onDoubleClick={() => canEdit && startEdit()}
              title={canEdit ? 'Doppelklick zum Bearbeiten' : ''}
            >
              {todo.title}
              <TodoSectionTag name={todo.section_name} />
            </div>
            {todo.description && (
              <div style={{ fontSize: 12, color: 'var(--color-text-strong)', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                {todo.description}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 3 }}>
              {todo.created_by_abbr ?? '—'} · {dayjs(todo.created_at).format('DD.MM.YYYY')}
              {isDone && ` → erledigt ${dayjs(todo.completed_at!).format('DD.MM.YYYY')} (${todo.completed_by_abbr ?? '—'})`}
            </div>
          </>
        )}
      </div>
      {!editing && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {canEdit && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={startEdit}
              title="Bearbeiten"
            >
              Bearb.
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onDelete}
              title="Löschen"
              style={{ color: 'var(--color-danger-text)' }}
            >
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}
